import type React from 'react';
import { createSlot } from '@ayocore/platform/client/extensions/createSlot';
import type { PartnerExtended } from './PartnerViewController';

/**
 * Активный лид партнёра для слотов расширений.
 * «Активный» = не архивный И статус не финальный — та же семантика,
 * что у серверного правила lead_active_uniqueness_rules.
 */
export interface PartnerActiveLead {
  id: number;
  title: string;
  leadTypeSlug?: string | null;
  leadTypeName?: string | null;
}

/** Payload, передаваемый во все слоты PartnerView */
export interface PartnerViewPayload {
  partner: PartnerExtended;
  reload: () => void;
  /** Есть ли у текущего пользователя права на редактирование */
  canEdit: boolean;
  /** Активные лиды партнёра (не архивные, со не-финальным статусом) в разрезе типов */
  activeLeads: PartnerActiveLead[];
}

/** Описание дополнительной вкладки */
export interface PartnerTabExtension {
  /** Уникальный ключ вкладки */
  value: string;
  /** Отображаемый заголовок вкладки */
  label: string;
  /** Содержимое панели вкладки */
  content: React.ReactNode;
}

/**
 * Ключи встроенных блоков главной вкладки PartnerView.
 * Используются в `hideMainPanelBlocks` для скрытия конкретных секций.
 */
export type PartnerMainPanelBlockKey =
  | 'leadFunnels'
  | 'info'
  | 'tasks'
  | 'channels'
  | 'contactPersons'
  | 'meta';

/**
 * Точки расширения PartnerView.
 *
 * Позволяют клиентским модулям управлять поведением PartnerView
 * без изменения базового кода CRM модуля.
 *
 * Регистрация происходит в bootstrap.ts клиентского модуля.
 *
 * @example
 * // src/modules/grasias/client/bootstrap.ts
 * import { PartnerViewExtensions } from '@ayocore/erp/crm/client/components/Partners/views/PartnerViewExtensions';
 *
 * PartnerViewExtensions.hideEventList.register(() => true);
 *
 * PartnerViewExtensions.tabs.register(({ partner }) => ({
 *   value: 'my-tab',
 *   label: 'Мой таб',
 *   content: <MyComponent partner={partner} />,
 * }));
 *
 * // Скрыть встроенные блоки главной вкладки (напр. контактные лица):
 * PartnerViewExtensions.hideMainPanelBlocks.register(() => ['contactPersons']);
 *
 * // Добавить свой блок в конец главной вкладки:
 * PartnerViewExtensions.mainPanelBlocks.register(({ partner }) => (
 *   <MyExtraBlock partnerId={partner.id} />
 * ));
 */
export const PartnerViewExtensions = {
  /**
   * Дополнительные кнопки в action bar заголовка карточки (рядом с Badge и Menu).
   * В отличие от `menuItems`, эти ноды рендерятся как inline-элементы — обычно
   * это `<Button size='xs'>` или `<ActionIcon>`. Используются для действий,
   * которые должны быть видны сразу, а не спрятаны в выпадающем меню.
   */
  headerActions: createSlot<PartnerViewPayload>(),

  /** Дополнительные вкладки (добавляются после встроенных) */
  tabs: createSlot<PartnerViewPayload, PartnerTabExtension | null>(),

  /**
   * Скрытие панели событий (event list).
   * Если хотя бы один обработчик возвращает true — event list не отображается.
   */
  hideEventList: createSlot<PartnerViewPayload, boolean>(),

  /**
   * Скрытие встроенных блоков главной вкладки по ключам.
   * Ключи всех регистраций объединяются; блоки с указанными ключами не рендерятся.
   */
  hideMainPanelBlocks: createSlot<PartnerViewPayload, PartnerMainPanelBlockKey[]>(),

  /** Дополнительные блоки в конце главной вкладки (после встроенных). */
  mainPanelBlocks: createSlot<PartnerViewPayload>(),

  /**
   * Дополнительные пункты в выпадающем меню действий заголовка карточки.
   * Каждый обработчик должен возвращать React-узел в виде `<Menu.Item>...</Menu.Item>`,
   * чтобы корректно встроиться в `Menu.Dropdown` PartnerHeader.
   *
   * @example
   * PartnerViewExtensions.menuItems.register(({ partner, reload }) => (
   *   <Menu.Item leftSection={<IconUnlink size={14} />} onClick={() => unlink1c(partner.id, reload)}>
   *     Отвязать 1С
   *   </Menu.Item>
   * ));
   */
  menuItems: createSlot<PartnerViewPayload>(),
};
